import { useInternalAdmin } from 'hooks'
import { useCallback, useEffect, useRef } from 'react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import dashboardApi from 'services/api/dashboard'

import ConfirmationDialog from '../../../components/ReusableComponents/ConfirmationDialog/index'
import CreditCardIcon from '../../../components/ReusableComponents/CreditCardIcon/index'
import LoadingIndicator from '../../../components/ReusableComponents/LoadingIndicator'
import Toggle from '../../../components/ReusableComponents/Toggle'
import HistoryIcon from '../components/PaymentMethodStatus/history.svg'
import PaymentMethodHistoryDialog from '../components/PaymentMethodStatus/PaymentMethodHistoryDialog'
import PaymentMethodStatusDialog from '../components/PaymentMethodStatus/PaymentMethodStatusDialog'
import usePaymentMethodStatus from '../components/PaymentMethodStatus/usePaymentMethodStatus'
import { ReactComponent as AddIcon } from './add.svg'
import { ReactComponent as CheckIcon } from './checkIcon.svg'
import styles from './ManagePaymentMethods.module.scss'

const Row = ({ children }) => {
  return <div className={styles.row}>{children}</div>
}

const paymentMethodNames = {
  CreditCard: 'Credit card',
  Check: 'Check',
  ACH: 'ACH'
}

const paymentMethodsCanBeSetAsDefault = ['CreditCard', 'ACH']

const ManagePaymentMethods = () => {
  const [isLoading, setLoading] = useState(true)
  const [savedPaymentMethods, setSavedPaymentMethods] = useState()
  const [methodInProgress, setMethodInProgress] = useState(null)
  const requestInProgress = useRef(false)

  const isInternalAdmin = useInternalAdmin()

  const getData = useCallback(async () => {
    const data = await dashboardApi.getSavedPaymentMethods()
    setSavedPaymentMethods(data || [])
    setLoading(false)
  }, [])

  const {
    closeHistoryDialog,
    closeStatusDialog,
    confirmStatus,
    historyDialog,
    openHistoryDialog,
    openStatusDialog,
    savingPaymentAccountId,
    statusDialog,
    statusError
  } = usePaymentMethodStatus(getData)

  useEffect(() => {
    getData()
  }, [getData])

  async function handleMakeDefault(id) {
    if (requestInProgress.current) {
      return
    }

    requestInProgress.current = true
    setMethodInProgress(id)

    try {
      await dashboardApi.makePaymentMethodDefaultNew(id)
      await getData()
    } finally {
      requestInProgress.current = false
      setMethodInProgress(null)
    }
  }

  if (statusDialog) {
    return (
      <PaymentMethodStatusDialog
        error={statusError}
        isDisabling={statusDialog.isDisabling}
        isSaving={savingPaymentAccountId === statusDialog.paymentAccountId}
        onClose={closeStatusDialog}
        onConfirm={confirmStatus}
      />
    )
  }

  if (historyDialog) {
    return (
      <PaymentMethodHistoryDialog
        error={historyDialog.error}
        history={historyDialog.history}
        isLoading={historyDialog.isLoading}
        onClose={closeHistoryDialog}
      />
    )
  }

  return (
    <ConfirmationDialog
      title="Edit payment methods"
      theme="blacklist"
      closeLink="/billing"
      confirmLink="/billing"
      maxWidth={680}
    >
      <div className={styles.wrapper}>
        {isLoading && <LoadingIndicator />}
        {!isLoading && (
          <section>
            <p className={styles.title}>Saved methods</p>
            {savedPaymentMethods.length === 0 && <div>No saved methods found</div>}
            {savedPaymentMethods &&
              savedPaymentMethods.length > 0 &&
              savedPaymentMethods
                .filter(m => m.network !== 'CA')
                .filter(m => (isInternalAdmin ? true : m.method !== 'Check'))
                .sort(m => (m.method === 'ACH' ? -1 : 1))
                .map(method => (
                  <Row key={method.paymentAccountId}>
                    <div className={styles.ccIcon}>
                      {method.method === 'ACH' ? (
                        <CheckIcon />
                      ) : (
                        <CreditCardIcon type={method.network} />
                      )}
                    </div>
                    <div className={styles.rowContent}>
                      <div className={styles.rowTitle}>
                        {paymentMethodNames[method.method]}
                      </div>
                      <div className={styles.rowDesc}>
                        {method.friendlyDisplayName}{' '}
                        {method.method === 'CreditCard' &&
                          `- ${method.accountHolderName} - ${method.expirationDate}`}
                      </div>
                      {method.isDisabled && (
                        <div className={styles.disabledDetails}>
                          <span className={styles.disabledBadge}>Disabled</span>
                          {(method.disableSource || method.disableNote) && (
                            <span>
                              {method.disableSource}
                              {method.disableSource && method.disableNote ? ': ' : ''}
                              {method.disableNote}
                            </span>
                          )}
                        </div>
                      )}
                      {method.isDefaultAccount && method.isDisabled && (
                        <div className={styles.defaultWarning}>
                          Autocharge will not run until this method is enabled or another
                          method is default.
                        </div>
                      )}
                    </div>
                    <div className={styles.rowAction}>
                      {method.isDefaultAccount ? (
                        <span className={styles.defaultMethod}>Default method</span>
                      ) : (
                        <>
                          {!method.isDisabled &&
                            paymentMethodsCanBeSetAsDefault.includes(method.method) && (
                              <button
                                disabled={Boolean(methodInProgress)}
                                onClick={() => handleMakeDefault(method.paymentAccountId)}
                              >
                                {methodInProgress === method.paymentAccountId
                                  ? 'Updating...'
                                  : 'Make default'}
                              </button>
                            )}
                        </>
                      )}
                      <div className={styles.statusControls}>
                        <Toggle
                          id={`payment-method-${method.paymentAccountId}`}
                          label="Allow automatic charges with this payment method"
                          labelClass={styles.toggleLabel}
                          checked={!method.isDisabled}
                          disabled={Boolean(methodInProgress || savingPaymentAccountId)}
                          handleChange={() => openStatusDialog(method)}
                          mini
                        />
                        <button
                          type="button"
                          className={styles.historyButton}
                          onClick={() => openHistoryDialog(method.paymentAccountId)}
                          aria-label={`View ${
                            paymentMethodNames[method.method]
                          } disable history`}
                          disabled={
                            historyDialog?.paymentAccountId === method.paymentAccountId &&
                            historyDialog.isLoading
                          }
                        >
                          <img src={HistoryIcon} alt="" />
                        </button>
                      </div>
                    </div>
                  </Row>
                ))}
            <Link className={styles.addPaymentButton} to={`/billing/addMethod/check`}>
              <AddIcon /> Add payment method
            </Link>
          </section>
        )}
        {/* <section>
          <p className={styles.title}>
            <RepeatIcon />
            Auto payments
          </p>
          <Row>
            <div className={styles.toggleHolder}>
              <Toggle />
            </div>
            <div className={styles.rowContent}>
              <div className={styles.rowTitle}>Enabled</div>
              <div className={styles.rowDesc}>Next payment on May 12</div>
            </div>
          </Row>
        </section> */}
      </div>
    </ConfirmationDialog>
  )
}

export default ManagePaymentMethods
